import { FC, ReactNode } from 'react';
import { Modal, Text, Group, Button, Stack } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface ConfirmCloseDialogProps {
  opened: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: string;
  size?: string;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  zIndex?: number;
}

export const ConfirmCloseDialog: FC<ConfirmCloseDialogProps> = ({
  opened,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmColor = 'red',
  size = 'sm',
  closeOnClickOutside = false,
  closeOnEscape = false,
  zIndex = 2000,
}) => {
  const { t } = useTranslation('common');

  // Default title with warning icon
  const defaultTitle = (
    <Group gap="xs">
      <IconAlertTriangle size={24} color="var(--mantine-color-orange-6)" />
      <Text fw={600}>{t('confirmClose.title')}</Text>
    </Group>
  );

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={title ?? defaultTitle}
      size={size}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      centered
      zIndex={zIndex}
    >
      <Stack gap="md">
        <Text>{message ?? t('confirmClose.message')}</Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel}>
            {cancelLabel ?? t('confirmClose.cancelLabel')}
          </Button>
          <Button color={confirmColor} onClick={onConfirm}>
            {confirmLabel ?? t('confirmClose.confirmLabel')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ConfirmCloseDialog;
